import { get, set, throttle, debounce } from 'lodash';
import awesomeDebouncePromise from 'awesome-debounce-promise';
import * as React from 'react';

import { ThemeContext } from 'styled-components';
import { canUseDOM } from 'src/helpers';

export * from './useStateCallback';
export * from './useUpdateEffect';
export * from './useCookie';
export * from './useMergeRefs';
export * from './useSearchParams';
export * from './useElementWidth';
export * from './useElementHeight';
export * from './useFormikAutoFocus';
export * from './useFileUploader';

export function useDebouncedFunction(func: (...args) => any, ms: number, opts?: any) {
  return React.useRef(awesomeDebouncePromise(func, ms, opts)).current;
}

export function useThrottleEffect(func: () => void, ms: number, deps?: any[], opts?: any) {
  const debouncedFunc = React.useRef(throttle(func, ms, opts));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => debouncedFunc.current(), deps);
}

export function useDebouncedEffect(func: () => void, ms: number, deps?: any[], opts?: any) {
  const debouncedFunc = React.useRef(debounce(func, ms, opts));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => debouncedFunc.current(), deps);
}

export function useLocalStorage<T>(keyPath: string, initialValue: T): [T, (value: T) => void] {
  const [key, ...restPath] = keyPath.split('.');

  const [state, updateState] = React.useState(
    (): T => {
      try {
        const localStorageKeyValue = JSON.parse(window.localStorage.getItem(key));
        if (localStorageKeyValue === null) {
          // Initialize local storage with default state
          window.localStorage.setItem(
            key,
            restPath.length ? JSON.stringify(set({}, restPath, initialValue)) : JSON.stringify(initialValue)
          );
          return initialValue;
        } else {
          try {
            const localStorageValue = restPath.length ? get(localStorageKeyValue, restPath) : localStorageKeyValue;
            if (localStorageValue === undefined) {
              window.localStorage.setItem(key, JSON.stringify(set(localStorageKeyValue, restPath, initialValue)));
              return initialValue;
            }
            return localStorageValue;
          } catch {
            return localStorageKeyValue as any;
          }
        }
      } catch {
        // User might be facing storage restrictions, or JSON
        // serialization/deserialization may have failed. We can just fall back
        // to using React state here.
        return initialValue;
      }
    }
  );
  const localStorageChanged = (e: StorageEvent) => {
    if (e.key === key) {
      updateState(restPath.length ? get(JSON.parse(e.newValue as string), restPath) : e.newValue);
    }
  };
  const setState = React.useCallback(
    (value: T) => {
      if (restPath.length) {
        const localStorageKeyValue = window.localStorage.getItem(key);
        window.localStorage.setItem(
          key,
          JSON.stringify(set(JSON.parse(localStorageKeyValue as string), restPath, value))
        );
      } else {
        window.localStorage.setItem(key, JSON.stringify(value));
      }
      updateState(value);
    },
    [key, restPath, updateState]
  );
  React.useEffect(() => {
    window.addEventListener('storage', localStorageChanged);
    return () => {
      window.removeEventListener('storage', localStorageChanged);
    };
  }, [localStorageChanged]);

  if (!canUseDOM()) {
    // We're SSRing; can't use local storage here!
    return [initialValue, () => {}];
  }

  return [state, setState];
}

export const useEnv = () => {
  const [isClient, toggleClient] = React.useState(false);
  React.useEffect(() => {
    toggleClient(true);
  }, []);
  return [isClient];
};

export const useThemeContext = () => React.useContext(ThemeContext);

export const useMediaQuery = query => {
  const [match, setMatch] = React.useState(window.matchMedia(query).matches);

  React.useEffect(() => {
    const mql = window.matchMedia(query);
    const updateMatch = () => setMatch(mql.matches);

    updateMatch();
    mql.addListener(updateMatch);
    return () => {
      mql.removeListener(updateMatch);
    };
  }, [query]);

  return match;
};

export const useViewMode = () => {
  const theme = useThemeContext();
  const query = `(min-width: ${theme.breakpoints.md}px)`;
  const isDesktopMode = useMediaQuery(query);
  const [viewMode, setViewMode] = React.useState({
    isDesktopMode,
    isMobileMode: !isDesktopMode
  });
  React.useEffect(() => {
    setViewMode({ isDesktopMode, isMobileMode: !isDesktopMode });
  }, [isDesktopMode]);

  return viewMode;
};

export const useCloseOnClickOutside = () => {
  const ref = React.useRef<any>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const toggleOpen = React.useCallback(() => setIsOpen(prevState => !prevState), []);

  const handleClickOutside = (event: Event) => {
    if (ref && ref.current && !ref.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  return { ref, isOpen, toggleOpen };
};
